// Third-party
import React from 'react';
import round from 'lodash/round';

// Components
import GenericHolding from '../holdings-editor/GenericHolding';

// Enums
import { HoldingAllocationMode } from '@/enums/Portfolio';

// Utils
import disclaimers from '../../../utils/disclaimers';
import { DISCLAIMER_KEYS } from '../../../utils/disclaimers/constants';
import { HOLDING_TYPES } from '../../../utils/portfolios';
import { fundToComparisonItem, getAssetDetailsForRiskComparison, stockToComparisonItem, RISK_ALLOCATION_KEYS } from '../../../utils/comparison';
import { formatMessage } from '../../../utils/i18n';
import { getLatestPriceForHolding } from '../../../utils/calculation-utils';
import Dictionary from '@/types/dictionary';
import PortfolioHolding from '@/types/portfolio-holding';
import AppRegion from '@/types/app-region';
import Holding from '@/types/holding';
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import ThemeMode from '@/types/theme-mode';
import altToComparisonItem from '@/utils/comparison/instrument-to-comparison-item/alt-to-comparison-item';
import Alt from '@/types/models/alt';

type Props = {
  appRegion: AppRegion;
  compact?: boolean;
  className?: string;
  portfolioHolding: PortfolioHolding;
  portfolioSize?: number;
  onSymbolChange: (holding: PortfolioHolding, value: string) => void;
  onWeightChange: (holding: PortfolioHolding, value: number) => void;
  onDollarsChange: (holding: PortfolioHolding, value: number) => void;
  onUnitsChange: (holding: PortfolioHolding, value: number) => void;
  onRemoveHolding: (holding: PortfolioHolding) => void;
  dark?: boolean;
  theme?: ThemeMode;
  showAssetDetails?: boolean;
  splitCashAndFixedIncome?: boolean;
  showDollars: boolean;
  data?: Holding | null;
  readOnly?: boolean;
  showCurrencies: boolean;
  allocationMode?: HoldingAllocationMode;
  selected?: boolean;
  onChangeHoldingMissing: (holding: PortfolioHolding, selected: boolean) => void;
  shelfName?: string;
};

// Disclaimer keys displayed on the holding, in order of importance.
const DISPLAYED_DISCLAIMERS = [
  DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_MISSING,
  DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_NO_DATA,
  DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_GIC,
  DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_HISA,
  DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_CLOSED,
  DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_LESS_THAN_1Y,
  DISCLAIMER_KEYS.EDIT_PORTFOLIO_HOLDING_LESS_THAN_3Y,
];

class EditPortfolioHolding extends React.Component<Props> {
  holdingRef = React.createRef<GenericHolding>();

  onSymbolChange = (symbol: string) => {
    const { portfolioHolding } = this.props;
    this.props.onSymbolChange(portfolioHolding, symbol);
  };

  onWeightChange = (weight: number) => {
    const { portfolioHolding } = this.props;

    const newWeight = Math.max(0, Math.min(100, weight));
    this.props.onWeightChange(portfolioHolding, newWeight);
  };

  onDollarsChange = (dollars: number) => {
    const { portfolioHolding } = this.props;

    this.props.onDollarsChange(portfolioHolding, dollars);
  };

  onUnitsChange = (units: number) => {
    const { portfolioHolding, data } = this.props;

    if (!data) {
      return;
    }

    this.props.onUnitsChange(portfolioHolding, units);
  };

  onRemoveHolding = () => {
    const { portfolioHolding } = this.props;

    this.props.onRemoveHolding(portfolioHolding);
  };

  onChangeHoldingMissing = () => {
    this.props.onChangeHoldingMissing(this.props.portfolioHolding, !this.props.selected);
  };

  getHoldingAssetDetails = (): Dictionary<number> => {
    const { portfolioHolding, data, appRegion, splitCashAndFixedIncome } = this.props;

    if (!data) {
      return {};
    }

    let item;

    switch (portfolioHolding.original.type) {
      case HOLDING_TYPES.FUND:
      case HOLDING_TYPES.CUSTOM_YIELD: {
        item = fundToComparisonItem(data as Fund, appRegion);
        break;
      }
      case HOLDING_TYPES.STOCK: {
        item = stockToComparisonItem(data as Stock, undefined, undefined, appRegion);
        break;
      }
      case HOLDING_TYPES.ALT: {
        item = altToComparisonItem(data as Alt);
        break;
      }
      default: {
        throw new Error('Invalid portfolio holding type');
      }
    }

    return getAssetDetailsForRiskComparison(item, splitCashAndFixedIncome);
  };

  getHoldingDisclaimer = () => {
    // Find first key that matches an active disclaimer on the holding.
    const { data, portfolioHolding } = this.props;
    const holdingDisclaimers = disclaimers.getPortfolioHoldingDisclaimers(portfolioHolding, data || undefined);

    const disclaimerKey = DISPLAYED_DISCLAIMERS.find(key => !!holdingDisclaimers[key]);

    if (disclaimerKey) {
      return holdingDisclaimers[disclaimerKey];
    }

    return undefined;
  };

  formatAllocation = (value?: number) => `${round(value || 0, 2)}%`;

  getAssetDetailsColumns = () => {
    const { showAssetDetails, splitCashAndFixedIncome } = this.props;
    const assetDetails = this.getHoldingAssetDetails();

    if (!showAssetDetails) {
      return undefined;
    }

    return (
      <>
        {splitCashAndFixedIncome && (
          <div className="column cash">
            <span>{this.formatAllocation(assetDetails[RISK_ALLOCATION_KEYS.CASH])}</span>
          </div>
        )}

        <div className="column fixed-income">
          <span>{this.formatAllocation(assetDetails[RISK_ALLOCATION_KEYS.FIXED_INCOME])}</span>
        </div>

        <div className="column equity">
          <span>{this.formatAllocation(assetDetails[RISK_ALLOCATION_KEYS.EQUITY])}</span>
        </div>

        <div className="column other">
          <span>{this.formatAllocation(assetDetails[RISK_ALLOCATION_KEYS.OTHER])}</span>
        </div>
      </>
    );
  };

  render() {
    const {
      portfolioHolding,
      dark,
      theme,
      showDollars,
      portfolioSize,
      readOnly,
      showCurrencies,
      allocationMode,
      data,
      selected,
      className,
      compact,
      shelfName,
    } = this.props;

    const { currency, name, symbol, weight, dollars, units, flags } = portfolioHolding;

    let price = null;
    if (data) {
      price = getLatestPriceForHolding(portfolioHolding.original.type, data);
    }

    return (
      <div className="edit-portfolio-holding">
        <GenericHolding
          flags={flags}
          ref={this.holdingRef}
          className={className}
          currency={currency}
          compact={compact}
          name={name}
          symbols={data && (data as Fund).symbols ? (data as Fund).symbols : undefined}
          symbol={symbol}
          weight={weight}
          dollars={dollars}
          units={units}
          onSymbolChange={this.onSymbolChange}
          onWeightChange={this.onWeightChange}
          onDollarsChange={this.onDollarsChange}
          onRemoveHolding={this.onRemoveHolding}
          onUnitsChange={this.onUnitsChange}
          unitsIsDisabled={!price}
          dark={dark}
          theme={theme}
          allocationMode={allocationMode}
          additionalColumns={this.getAssetDetailsColumns()}
          holdingDisclaimer={this.getHoldingDisclaimer()}
          showDollars={showDollars}
          readOnly={readOnly}
          showCurrencies={showCurrencies}
          weightDisabled={!portfolioSize}
          weightDisabledTooltip={formatMessage('HoldingsEditor.WeightDisabled')}
          onChangeHoldingMissing={this.onChangeHoldingMissing}
          selected={selected}
          shelfName={shelfName}
        />
      </div>
    );
  }
}

export default EditPortfolioHolding;
